import Vue from 'vue'
import { isSamePath as _isSamePath, joinURL, normalizeURL, withQuery, withoutTrailingSlash } from 'ufo'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function createGetCounter (counterObject, defaultKey = '') {
  return function getCounter (id = defaultKey) {
    if (counterObject[id] === undefined) {
      counterObject[id] = 0
    }
    return counterObject[id]++
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function purifyData(data) {
  if (process.env.NODE_ENV === 'production') {
    return data
  }

  return Object.entries(data).filter(
    ([key, value]) => {
      const valid = !(value instanceof Function) && !(value instanceof Promise)
      if (!valid) {
        console.warn(`${key} is not able to be stringified. This will break in a production environment.`)
      }
      return valid
    }
    ).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        try {
          Component = await Component()
        } catch (error) {
          // Handle webpack chunk loading errors
          // This may be due to a new deployment or a network problem
          if (
            error &&
            error.name === 'ChunkLoadError' &&
            typeof window !== 'undefined' &&
            window.sessionStorage
          ) {
            const timeNow = Date.now()
            const previousReloadTime = parseInt(window.sessionStorage.getItem('nuxt-reload'))

            // check for previous reload time not to reload infinitely
            if (!previousReloadTime || previousReloadTime + 60000 < timeNow) {
              window.sessionStorage.setItem('nuxt-reload', timeNow)
              window.location.reload(true /* skip cache */)
            }
          }

          throw error
        }
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: app.router.options.base,
      env: {"theme":"men","BUILD_ENV":"production","MOBILE_APP_BANNER":"[\"ios\",\"android\"]","SITE_VERSION":"default","SHOPIFY_URL":"https://fr.asphalte.com","SHOPIFY_API_VERSION":"2024-01","SHOPIFY_STOREFRONT_ACCESS_TOKEN":"0122469f66d723cc2bca88f3a340cbd4","SHOPIFY_STOCK_COLLECTION_UID":{"fr-FR":{"h":608118997325,"f":608182305101},"en-US":{"h":625715183949},"de-DE":{"h":625715183949,"f":638053646669}},"PRISMIC_ENDPOINT":"asphalte","PRISMIC_URL":"https://asphalte.cdn.prismic.io/api/v2","PRISMIC_GRAPHQL_URL":"https://asphalte.cdn.prismic.io/graphql","PRISMIC_ACCESS_TOKEN":"MC5ZWjV4RWhJQUFDRUFnbmFI.77-9MhADce-_vRDvv70Z77-9QRjvv73vv73vv70fbu-_ve-_vWjvv73vv73vv71B77-9YlJ-Tu-_vRjvv70","AVIS_CLIENT_URL":"https://avis.asphalte.com/api","ACCOUNT_MANAGER_URL":"https://account.asphalte.com","BIS_URL":"https://notification.asphalte.com","GOOGLE_TAG_MANAGER_ID":"GTM-TG62QWR","LOAD_GTM":"false","DEFAULT_LOCALE":"fr-FR","KLAVIYO_API_KEY":"PZmQi7","BASE_URL":"https://www.asphalte.com","SHOPIFY_SUB_DOMAIN":"asphalte","PARRAINAGE_URL":"https://parrainage.asphalte.com","SHOPIFY_PRINT_ID":"c571dc81c1601b71ec5d","SHOPIFY_PRINT_TIMES":"5696","IP_ADDRESS_URL":"https://api.ipify.org?format=json","PARRAINAGE_PRICE":"15","PARRAINAGE_FILLEUL_PRICE":"15","FREE_SHIPPING_PRICE":"0","ALMOST_SOLD_OUT_LIMIT":20,"CLARITY_KEY":"oyzdr3e9xu","UPSELLING_MAX_ITEMS":"10","DEFAULT_GALLERIES":{"desktop":["3 produits"],"mobile":["2 produits"]},"UPSELLING_GROUPS":[{"type":"products","canCompleteList":false,"name":"Upsell Test Pull","weight":1,"context":"h","products":[{"id":9969368465741,"slug":{"fr":"le-pull-parfait","en":"the-perfect-jumper","de":"der-perfekte-pulli"}},{"id":9969393271117,"slug":{"fr":"le-t-shirt-ultime","en":"the-ultimate-t-shirt","de":"das-ultimative-t-shirt"}}],"tag":{}},{"type":"products","canCompleteList":false,"name":"Upsell Test Jean","weight":1,"context":"h","products":[{"id":9969393271117,"slug":{"fr":"le-jean-ultime","en":"the-ultimate-jeans","de":"die-ultimativen-jean"}},{"id":9932254150989,"slug":{"fr":"la-bonne-ceinture","en":"the-good-belt","de":" der-gute-gurtel"}}],"tag":{}},{"type":"products","canCompleteList":false,"name":"Upsell Test Chaussures 2","weight":1,"context":"h","products":[{"id":9756730196301,"slug":{"fr":"les-baskets-triple-a","en":"the-triple-a-sneakers","de":"die-triple-a-sneaker"}},{"id":9969393271117,"slug":{"fr":"le-jean-ultime","en":"the-ultimate-jeans","de":"die-ultimativen-jean"}}],"tag":{}},{"type":"products","canCompleteList":false,"name":"Upsell Test Chaussures 1","weight":1,"context":"h","products":[{"id":9969520836941,"slug":{"fr":"les-sneakers-solides","en":"the-solid-sneakers","de":"die-soliden-sneaker"}},{"id":9969393271117,"slug":{"fr":"le-jean-ultime","en":"the-ultimate-jeans","de":"die-ultimativen-jean"}}],"tag":{}},{"type":"products","canCompleteList":false,"name":"Le Costume Slack","weight":2,"context":"h","products":[{"id":6633231548510,"slug":{"fr":"la-housse-durable-preco","en":"the-garment-sleeve-preco","de":"der-kleidersack-fur-anzug-preco"}}],"tag":{}},{"type":"products","canCompleteList":true,"name":"Goodies Femme","weight":3,"context":"f","products":[{"id":9853723803981,"slug":{"fr":"le-porte-cles-f"}},{"id":9853697622349,"slug":{"fr":"le-tote-bag-f"}}],"tag":{}},{"type":"products","canCompleteList":true,"name":"Goodies Homme","weight":3,"context":"h","products":[{"id":9853723803981,"slug":{"fr":"le-porte-cles","en":"the-key-holder","de":"der-schlusselanhanger"}},{"id":9853697622349,"slug":{"fr":"le-tote-bag","en":"the-tote-bag","de":"die-tragetasche"}}],"tag":{"fr":"Aussi en stock"}},{"type":"products","canCompleteList":true,"name":"Préco Ephémère Femme","weight":1,"context":"f","products":[],"tag":{}},{"type":"products","canCompleteList":true,"name":"Préco Ephémère Homme","weight":2,"context":"h","products":[],"tag":{}},{"type":"products","canCompleteList":true,"name":"Carte Cadeau ","weight":4,"context":"h","products":[{"id":1598782406750,"slug":{"fr":"carte-cadeau","en":"the-gift-card","de":"der-geschenkgutschein"}}],"tag":{"fr":"Plaisir d’offrir"}},{"type":"link","canCompleteList":false,"weight":0,"origins":[6738298601566],"target":{"id":6665177268318,"slug":{"fr":"la-housse-durable"}},"tag":{}},{"type":"products","canCompleteList":true,"name":"La femme","weight":2,"context":"f","products":[{"id":9971103367501,"slug":{"fr":"le-t-shirt-ultime-f","de":"das-ultimative-t-shirt-f"}},{"id":9936758473037,"slug":{"fr":"les-chaussettes-parfaites-f","de":"die-perfekten-socken-f"}},{"id":9919050678605,"slug":{"fr":"les-socquettes-solides-f"}},{"id":9971128795469,"slug":{"fr":"le-pull-parfait-f","de":"der-perfekte-pulli-f"}},{"id":8912298541389,"slug":{"fr":"le-jean-ultime-slim-f","de":"die-ultimativen-jeans-slim-fit-f"}}],"tag":{}},{"type":"products","canCompleteList":true,"name":"Les éternels","weight":1,"context":"h","products":[{"id":9976313151821,"slug":{"fr":"le-t-shirt-ultime","en":"the-ultimate-t-shirt","de":"das-ultimative-t-shirt"}},{"id":9919050678605,"slug":{"fr":"les-socquettes-solides","en":"the-solid-no-show-socks","de":"die-soliden-fusslinge"}},{"id":9971076202829,"slug":{"fr":"les-chaussettes-parfaites","en":"the-perfect-socks","de":"die-perfekten-socken"}},{"id":9971060506957,"slug":{"fr":"le-boxer-confort","en":"the-boxer","de":"die-boxer"}},{"id":9969393271117,"slug":{"fr":"le-jean-ultime","en":"the-ultimate-jeans","de":"die-ultimativen-jean"}},{"id":9969368465741,"slug":{"fr":"le-pull-parfait","en":"the-perfect-jumper","de":"der-perfekte-pulli"}},{"id":9969412931917,"slug":{"fr":"le-sweat-parfait","en":"the-perfect-sweatshirt","de":"das-perfekte-sweatshirt"}},{"id":9969520836941,"slug":{"fr":"les-sneakers-solides","en":"the-solid-sneakers","de":"die-soliden-sneaker"}},{"id":9971040059725,"slug":{"fr":"la-chemise-oxford","en":"the-button-down-oxford","de":"das-oxford-hemd"}}],"tag":{}}],"ABTEST_UID_SWITCH":{"products":[{"experiment":"slider-t-shirt-femme","origin":"le-t-shirt-ultime-f","alter":"le-t-shirt-ultime-ab-test-f","locale":"fr"},{"experiment":"slider-t-shirt-homme","origin":"le-t-shirt-ultime","alter":"le-t-shirt-ultime-ab-test","locale":"fr"}],"collections":[]},"CODE_VERSION":"baldr-7.0.9","SANITY_PROJECT_ID":"qna1oyeq","SANITY_DATASET":"production","SANITY_API_TOKEN":"skuhp0WcNWkxJvIY80XgH5vops9922OjrlSGR4XNEL2Vd1NinkXAe0xYvSQeGesjVnBvUWCfl6NmyoNZX4qOYSVDAcbCGie6CIlACFvDjMfKe1KD3SjIKNzasg1mnkIRNXaMyIbZuMYwXC5gFv6iN9KiAtgvjnxFCgWQmYcCkvcOx7xjEBCG","BUNNY_CDN_HOSTNAME":"https://asphalte.b-cdn.net","BATCH_API_KEY":"E6789CD6521A4ECFA232223EBA6A59A5","BATCH_AUTH_KEY":"2.6g70sYGgcCJFdS+/r4ydNOWSeZAxu0sN0qn09EjyqmE=","BATCH_VAPID_KEY":"BH2O-vQLmFGEeXEHOFWVCymEyYVVrITjbzCIlqqhFBtl78MfNLxCHVyy2mhOgxTGY0nniQlk1Swc3k4cai1ZAdc","BATCH_SUBDOMAIN":"asphalte","ODIN_URL":"https://odin.asphalte.com","HUGIN_URL":"https://hugin.asphalte.com","FORSETI_URI":"https://forseti.asphalte.com","AXEPTIO_API_KEY":"61780c2ed79e541df367a715","STOCK_PRECO_BIBLIO_UID":{"fr-FR":"stock-explication","en-US":"stock-at-asphalte","de-DE":"lagerbestande-bei-asphalte"},"ABTASTY_ENVIRONNEMENT_ID":"cu3ngp3grjeh3ul97nl0","ABTASTY_API_KEY":"krrrAepejDmCElIWYKFKZOfxvBFpXagqLEAXfNUS"}
    }
    // Only set once

    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = withQuery(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/assign
          window.location.assign(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
      app.context.beforeSerialize = fn => context.beforeSerializeFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  if (context.error) {
    app.context.error = context.error
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext, renderState) {
  if (!promises.length || appContext._redirected || appContext._errored || (renderState && renderState.aborted)) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext, renderState)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }

  base = decodeURI(base).slice(0, -1) // consideration is base is normalized with trailing slash
  let path = decodeURI(window.location.pathname)

  if (base && path.startsWith(base)) {
    path = path.slice(base.length)
  }

  const fullPath = (path || '/') + window.location.search + window.location.hash

  return normalizeURL(fullPath)
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export const urlJoin = joinURL

export const stripTrailingSlash = withoutTrailingSlash

export const isSamePath = _isSamePath

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
